import { useDispatch } from "react-redux";
import {
  BreadCrumbs,
  Button,
  DatePicker,
  Pagination,
  ProtectedComponent,
  RenderName,
  TableHeader,
  TableRow,
  TextInput,
  UserSideBar,
} from "../../components";
import useBreadCrumbs from "../../hooks/useBreadCrumbs";
import { headers } from "./ViewPatientData";
import { AppDispatch, RootState } from "../../redux/store";
import { useContext, useEffect, useState } from "react";
import { getUserRequests } from "../../redux/slices/UserSlice";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { billedToOptions, formatOhipNumberNew, formatPhoneNumber, getPlanDetails, getSorting, renderState } from "../../utils";
import moment from "moment";
import { User } from "../../models/user.model";
import { Request } from "../../models/request.model";
import { sortObj } from "../../redux/slices/OfficeSlice";
import EditPatientProfile from "../../components/forms/EditPatientProfile/EditPatientProfile";
import { OfficeService } from "../../services/office.service";
import { AuthPermission } from "../../services/auth.service";
import DataNotFound from "../../components/DataNotFound/DataNotFound";
import NoRequestFound from "../../assets/images/no-result.png";
import { Add, DeleteOutline } from "@mui/icons-material";
import { FamilyRow } from "../PatientPlan/PlanSelection";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { updateFamilyMembers } from "../../redux/slices/annualPlanSlice";
import { ToastVariants } from "../../models";
import { PlanNames } from "../../models/annual-plan.model";

const ViewPatient = () => {
  const navigate = useNavigate();
  const { patientId, id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const { userRequest } = useSelector((state: RootState) => state.user);
  const { currentOffice } = useSelector((state: RootState) => state.office);
  const [user, setUser] = useState<User | null>(null);
  const [showEditPatientProfile, setShowEditPatientProfile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sortKeys] = useState(headers.map(head => head.sortKey));
  const [sortMethods, setSortMethods] = useState<boolean[]>(headers.map(() => false));
  const [activeSortIndex, setActiveSortIndex] = useState<number>(-1);
  const [editFamilyMembers, setEditFamilyMembers] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [familyRows, setFamilyRows] = useState<FamilyRow[]>([]);
  const [tableState, setTableState] = useState({
    start: 0,
    end: 10,
    pageNumber: 0,
    rowsPerPage: 10,
    sortedBy: "createdDate,desc",
  });

  let breadCrumbs = useBreadCrumbs();
  breadCrumbs = [...breadCrumbs.slice(0, breadCrumbs.length - 2), ...breadCrumbs.slice(breadCrumbs.length)];
  breadCrumbs = [
    ...breadCrumbs,
    {
      title: "Patients",
      key: id ? `/offices/${id}/patients` : "/patients",
    },
    {
      key: "as",
      title: `${user?.firstName ?? ""} ${user?.lastName ?? ""}`,
    },
  ];

  const handleRowClick = (requestId: string, officeId: string) => {
    navigate(`/offices/${officeId}/requests/${requestId}`);
  };

  const updateTable = (sortObj?: sortObj) => {
    const pageNumToBeCalled = sortObj?.pageNumber ?? tableState.pageNumber;
    const sizeToBeCalled = sortObj?.rowsPerPage ?? tableState.rowsPerPage;
    const sortBy = sortObj?.sortedBy ?? tableState.sortedBy;
    if (patientId) {
      dispatch(
        getUserRequests({
          page: pageNumToBeCalled,
          size: sizeToBeCalled,
          sort: sortBy,
          userId: patientId,
        })
      ).then(() => {
        setTableState({ ...tableState, pageNumber: pageNumToBeCalled, rowsPerPage: sizeToBeCalled, sortedBy: sortBy });
      });
    }
  };

  const getUser = async (officeId: string, patientId: string) => {
    const currentUser = await OfficeService.getOfficeUser(officeId, patientId);
    setUser(currentUser);
  };

  const load = async (patientId: string) => {
    dispatch(getUserRequests({ userId: patientId, page: 0, size: 10, sort: "createdDate,desc" }));
    // FIXME: Need office ID
    if (id) getUser(id, patientId);
  };

  const handleSort = (index: number) => {
    const sortMethodString = getSorting(sortKeys, index, sortMethods);
    setSortMethods([...sortMethods.slice(0, index), !sortMethods[index], ...sortMethods.slice(index + 1)]);
    setActiveSortIndex(index);
    updateTable({ sortedBy: sortMethodString, pageNumber: 0, rowsPerPage: tableState.rowsPerPage });
  };

  const onPageChange = (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    updateTable({ sortedBy: tableState.sortedBy, pageNumber: newPage, rowsPerPage: tableState.rowsPerPage });
  };

  const onRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    updateTable({ sortedBy: tableState.sortedBy, pageNumber: 0, rowsPerPage: parseInt(event.target.value, 10) });
  };

  const handleEditFamilyMembers = () => {
    const familyMembers = user?.userAnnualPlan?.additionalMembers
      ? user?.userAnnualPlan?.additionalMembers.split("$").map(item => {
          const [firstName, lastName, dob] = item.split("|");
          return { firstName, lastName, dob };
        })
      : [];
    setFamilyRows(familyMembers);
    setEditFamilyMembers(true);
  };

  const removeMember = (index: number) => {
    const updatedRows = [...familyRows];
    updatedRows.splice(index, 1);
    setFamilyRows(updatedRows);
  };

  const handleAddMember = async () => {
    const plan = user?.userAnnualPlan?.officeAnnualPlan.annualPlanType.name;
    const isFamily = plan && plan.toLocaleLowerCase() === "family";
    const familyCount = isFamily ? 6 : 2;
    if (familyRows.length >= (isFamily ? familyCount : 1) && plan) {
      toast?.openToast(`You can only have ${familyCount} family members with a ${plan.toLocaleLowerCase()} plan.`);
      return;
    }
    setFamilyRows([...familyRows, { firstName, lastName, dob }]);
    setFirstName("");
    setLastName("");
    setDob("");
  };

  const saveFamilyMembers = async () => {
    const family = familyRows?.length > 0 ? familyRows.map(item => `${item.firstName}|${item.lastName}|${item.dob}`).join("$") : undefined;
    if (!family) {
      toast?.errorToast("Please add atleast one family member.");
      return;
    }
    if (user?.userAnnualPlan?.additionalMembers === family) {
      toast?.errorToast("Nothing to update.");
      return;
    }
    if (id && user?.userId && user?.userAnnualPlan?.userAnnualPlanId) {
      setIsLoading(true);
      const res = await dispatch(
        updateFamilyMembers({ officeId: id, userId: user?.userId, userAnnualPlanId: user?.userAnnualPlan?.userAnnualPlanId, family })
      );

      if (res.payload) {
        await getUser(id, user?.userId);
        toast?.openToast("Family updated successfully!", 2000, ToastVariants.Success);
        setEditFamilyMembers(false);
        setFirstName("");
        setLastName("");
        setDob("");
      } else {
        toast?.errorToast("Family updated failed!");
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (patientId) {
      load(patientId);
    }
  }, [patientId]);

  const moreOptions = ["View Details"];
  const headerIconAsc = <p> ↑</p>;
  const headerIconDesc = <p> ↓</p>;
  const sortIcons = sortMethods.map((item, index) => (activeSortIndex === index ? (item ? headerIconAsc : headerIconDesc) : null));
  const familyMembers = user?.userAnnualPlan?.additionalMembers
    ? user.userAnnualPlan.additionalMembers.split("$").map(item => {
        const [firstName, lastName, dob] = item.split("|");
        return { firstName, lastName, dob };
      })
    : null;

  const plan = getPlanDetails(user?.userAnnualPlan?.officeAnnualPlan);
  return (
    <>
      <main>
        <div className="container mx-auto p-8">
          <BreadCrumbs items={breadCrumbs} />
          <div className="block lg:flex justify-between items-center mb-6">
            <div className="w-full mb-5 lg:mb-0">
              <h2 className="heading mb-0 uppercase">
                {user?.firstName ?? "-"} {user?.lastName ?? ""}
              </h2>
              {currentOffice?.annualPlanActive ? (
                <p className="font-medium">
                  Plan:{" "}
                  {user?.userAnnualPlan?.officeAnnualPlan.annualPlanType
                    ? `${user.userAnnualPlan.officeAnnualPlan.annualPlanType.name} (${user.userAnnualPlan.officeAnnualPlan.annualPlanType.subscriptionType})`
                    : user?.annualPlanMember
                    ? `Couple or family plan`
                    : "No active plan"}
                </p>
              ) : null}
            </div>
          </div>
          {/* Patient details section */}
          <ProtectedComponent requiredPermission={[AuthPermission.User]}>
            <div className="flex justify-end">
              <Button text="Edit Profile" onClick={() => setShowEditPatientProfile(true)} width="fit" />
            </div>
          </ProtectedComponent>
          <div className="bg-docsigna-pink-light p-5 rounded mt-5">
            <div className="pb-4">
              <h2 className="text-xl font-bold text-docsigna-blue-extra-light">Patient Details</h2>
              <div className="lg:flex justify-between gap-[34px]">
                <div className="py-3">
                  <h5 className="font-bold text-xl break-all">{user?.ohipNumber ? formatOhipNumberNew(user?.ohipNumber) : "-"}</h5>
                  <h3 className="text-sm font-medium">Health Card Number</h3>
                </div>
                <div className="bg-white w-[1px]" />
                <div className="py-3">
                  <h5 className="font-bold text-xl break-all">{user?.dateOfBirth ? moment(user?.dateOfBirth).format("ll") : "-"}</h5>
                  <h3 className="text-sm font-medium">Date of Birth</h3>
                </div>
                <div className="bg-white w-[1px]" />
                <div className="py-3">
                  <h5 className="font-bold text-xl break-all">{user?.email ? user?.email : "-"}</h5>
                  <h3 className="text-sm font-medium">Email Address</h3>
                </div>
                <div className="bg-white w-[1px]" />
                <div className="py-3">
                  <h5 className="font-bold text-xl break-all">{user?.phone ? formatPhoneNumber(user?.phone) : "-"}</h5>
                  <h3 className="text-sm font-medium">Phone Number</h3>
                </div>
              </div>
            </div>
            {currentOffice?.annualPlanActive ? (
              <div>
                <h2 className="text-xl font-bold text-docsigna-blue-extra-light">Annual Plan Details</h2>
                {!user?.userAnnualPlan && !user?.annualPlanMember ? <p>Patient not subscribed to the annual plan</p> : null}
                <div className="flex gap-[54px]">
                  {user?.annualPlanMember && !user?.userAnnualPlan ? (
                    <p className="text-sm font-medium py-3">Patient is included in couple or family plan</p>
                  ) : null}
                  <div className="bg-white w-[1px]" />
                  {user?.userAnnualPlanDate && (
                    <div className="py-3">
                      <h3 className="text-sm font-medium">Annual Plan Renewal Date</h3>
                      <h5 className="font-bold text-xl break-all capitalize">{user?.userAnnualPlanDate ?? "-"}</h5>
                    </div>
                  )}
                </div>
                {user?.userAnnualPlan ? (
                  <div>
                    <div className="flex justify-between flex-wrap">
                      <div className="py-3">
                        <h3 className="text-sm font-medium">Plan Type</h3>
                        <h5 className="font-bold text-xl break-all">{user?.userAnnualPlan?.officeAnnualPlan.annualPlanType.name ?? "-"}</h5>
                      </div>
                      <div className="py-3">
                        <h3 className="text-sm font-medium">Payment Frequency</h3>
                        <h5 className="font-bold text-xl break-all capitalize">
                          {user?.userAnnualPlan?.officeAnnualPlan.annualPlanType.subscriptionType ?? "-"}
                        </h5>
                      </div>
                      <div className="py-3">
                        <h3 className="text-sm font-medium">Annual Plan Start Date</h3>
                        <h5 className="font-bold text-xl break-all">
                          {user?.userAnnualPlan?.startDate ? moment(user?.userAnnualPlan?.startDate).format("ll") : "-"}
                        </h5>
                      </div>
                      <div className="py-3">
                        <h3 className="text-sm font-medium">Plan Status</h3>
                        <h5 className="font-bold text-xl break-all capitalize">{user?.userAnnualPlan?.userAnnualPlanState ?? "-"}</h5>
                      </div>
                    </div>
                    {plan?.planName && [PlanNames.Family, PlanNames.Couple, PlanNames["Senior Couple"]].includes(plan?.planName) ? (
                      <div className="flex justify-between flex-wrap">
                        <div>
                          <h3 className="text-sm font-medium">Family Members</h3>
                        </div>
                        {!editFamilyMembers ? (
                          <div className="w-full sm:flex flex-row justify-between">
                            <div className="flex flex-col gap-1">
                              {familyMembers ? (
                                familyMembers.map((member, i) => (
                                  <h5 key={i} className="font-bold text-lg break-all">{`${member.firstName} ${member.lastName}, ${moment(
                                    member.dob
                                  ).format("ll")}`}</h5>
                                ))
                              ) : (
                                <h5 className="font-bold text-xl break-all">-</h5>
                              )}
                            </div>
                            <div>
                              <Button text="Edit Family Members" onClick={handleEditFamilyMembers} />
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex flex-row justify-between flex-wrap gap-5 mb-5">
                              <div className="flex flex-wrap gap-5 mt-3">
                                <div>
                                  <TextInput
                                    isRequired
                                    label="First Name"
                                    placeholder="Enter first name"
                                    onChangeFunc={val => {
                                      const sanitizedValue = val.target.value.replace(/[$|]/g, "");
                                      setFirstName(sanitizedValue);
                                    }}
                                    value={firstName}
                                    isDarkBg
                                  />
                                </div>
                                <div>
                                  <TextInput
                                    isRequired
                                    label="Last Name"
                                    placeholder="Enter last name"
                                    onChangeFunc={val => {
                                      const sanitizedValue = val.target.value.replace(/[$|]/g, "");
                                      setLastName(sanitizedValue);
                                    }}
                                    value={lastName}
                                    isDarkBg
                                  />
                                </div>
                                <div>
                                  <DatePicker
                                    label={"Date of Birth"}
                                    isRequired={true}
                                    name={"dob"}
                                    placeholder={"DD/MM/YYYY"}
                                    value={dob}
                                    onChange={val => {
                                      const sanitizedValue = val.replace(/[$|]/g, "");
                                      setDob(sanitizedValue);
                                    }}
                                    errorMsg={""}
                                    isError={false}
                                    changeHeight={false}
                                    isDarkBg
                                    dateFormat="DD/MM/YYYY"
                                  />
                                </div>
                              </div>
                              <div className="flex gap-5">
                                <Button text="Cancel" disabled={isLoading} variant="Outlined" onClick={() => setEditFamilyMembers(false)} />
                                <Button
                                  text="Save"
                                  disabled={!familyRows.length || isLoading}
                                  onClick={saveFamilyMembers}
                                  variant={isLoading ? "loading" : "Primary"}
                                />
                              </div>
                            </div>
                            <div className="flex mb-5">
                              <Button
                                disabled={isLoading || !firstName.trim().length || !lastName.trim().length || !dob.trim().length}
                                icon={<Add fontSize="small" className="text-xs" />}
                                width="fit"
                                text="Add Family Member"
                                onClick={handleAddMember}
                              />
                            </div>
                            {familyRows?.length
                              ? familyRows.map((member, i) => (
                                  <div key={i} className="flex gap-10 justify-between w-1/2 border-b py-2">
                                    <h5 className="text-lg break-all w-1/3">
                                      {member.firstName} {member.lastName}
                                    </h5>
                                    <p className="w-1/3">{moment(member.dob).format("ll")}</p>
                                    <p className="cursor-pointer" onClick={() => removeMember(i)}>
                                      <DeleteOutline className="text-docsigna-orange" />
                                    </p>
                                  </div>
                                ))
                              : null}
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}

            {user?.deactivatedUserAnnualPlan ? (
              <div className="pt-4">
                <h2 className="text-xl font-bold text-docsigna-blue-extra-light">Deactivated Annual Plan Details</h2>
                <div>
                  <div className="flex justify-between flex-wrap">
                    <div className="py-3">
                      <h3 className="text-sm font-medium">Plan Type</h3>
                      <h5 className="font-bold text-xl break-all">{user?.deactivatedUserAnnualPlan?.officeAnnualPlan.annualPlanType.name ?? "-"}</h5>
                    </div>
                    <div className="py-3">
                      <h3 className="text-sm font-medium">Payment Frequency</h3>
                      <h5 className="font-bold text-xl break-all capitalize">
                        {user?.deactivatedUserAnnualPlan?.officeAnnualPlan.annualPlanType.subscriptionType ?? "-"}
                      </h5>
                    </div>
                    <div className="py-3">
                      <h3 className="text-sm font-medium">Annual Plan Start Date</h3>
                      <h5 className="font-bold text-xl break-all">
                        {user?.deactivatedUserAnnualPlan?.startDate ? moment(user?.deactivatedUserAnnualPlan?.startDate).format("ll") : "-"}
                      </h5>
                    </div>
                    <div className="py-3">
                      <h3 className="text-sm font-medium">Plan Status</h3>
                      <h5 className="font-bold text-xl break-all capitalize">{user?.deactivatedUserAnnualPlan?.userAnnualPlanState ?? "-"}</h5>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {user?.upcomingUserAnnualPlan ? (
              <>
                <div>
                  <h2 className="text-xl font-bold text-docsigna-blue-extra-light">New Annual Plan Details</h2>
                </div>
                <div className="flex justify-between flex-wrap">
                  <div className="py-3">
                    <h3 className="text-sm font-medium">Plan Type</h3>
                    <h5 className="font-bold text-xl break-all">{user?.upcomingUserAnnualPlan?.officeAnnualPlan.annualPlanType.name ?? "-"}</h5>
                  </div>
                  <div className="py-3">
                    <h3 className="text-sm font-medium">Payment Frequency</h3>
                    <h5 className="font-bold text-xl break-all capitalize">
                      {user?.upcomingUserAnnualPlan?.officeAnnualPlan.annualPlanType.subscriptionType ?? "-"}
                    </h5>
                  </div>
                  <div className="py-3">
                    <h3 className="text-sm font-medium">Annual Plan Start Date</h3>
                    <h5 className="font-bold text-xl break-all">
                      {user?.userAnnualPlan?.startDate ? moment(user?.upcomingUserAnnualPlan?.startDate).format("ll") : "-"}
                    </h5>
                  </div>
                  <div className="py-3">
                    <h3 className="text-sm font-medium">Plan Status</h3>
                    <h5 className="font-bold text-xl break-all capitalize">{user?.upcomingUserAnnualPlan?.userAnnualPlanState ?? "-"}</h5>
                  </div>
                </div>
              </>
            ) : null}
            <div className="lg:flex justify-between gap-[34px]">
              <div className="py-3">
                <h5 className="font-bold text-xl break-all">Notes</h5>
                <h3 className="text-sm font-medium break-all">{user?.note ?? "-"}</h3>
              </div>
            </div>
          </div>
          <div className="block">
            {/* Patient requests table */}
            <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50 mt-10">
              {headers.map((head, index) => (
                <TableHeader
                  title={head.title}
                  index={index}
                  key={index}
                  sortIcon={sortIcons[index]}
                  handleSort={handleSort}
                  extraClassesHeader={head.extraClasses}
                  isClickable={head.isClickable}
                />
              ))}
              <div className="w-auto p-5 py-3">
                <span className="text-base text-docsigna-purple-dark inline-block w-5"></span>
              </div>
            </div>
            {userRequest.data !== undefined && userRequest.data !== null && userRequest?.data?.content?.length > 0 ? (
              <>
                {userRequest.data.content.map((rowData: Request, index: number) => {
                  const textColor = "";
                  const sortedData = {
                    request: rowData.requestNumber ? <span className={textColor}>{rowData.requestNumber}</span> : "-",
                    service: (
                      <RenderName
                        extraClasses={"text-sm " + textColor}
                        title={rowData.service ? rowData.service.name : "--"}
                        subTitle={`PCP: ${
                          rowData.primaryCareProvider
                            ? `${rowData.primaryCareProvider.firstName} ${rowData.primaryCareProvider.lastName}`
                            : "Unassigned"
                        }`}
                      />
                    ),
                    billedto: (
                      <span className={textColor}>
                        {rowData?.serviceBilledToOverride
                          ? billedToOptions[rowData?.serviceBilledToOverride]
                          : rowData?.service?.serviceBilledTo
                          ? billedToOptions[rowData?.service?.serviceBilledTo]
                          : "-"}
                      </span>
                    ),
                    status: <span className={textColor}>{renderState[rowData.state]}</span>,

                    updated: <span className={textColor}>{moment(rowData.updatedDate).format("ll")}</span>,
                    comments: (
                      <>
                        <span className={textColor}>{rowData.numMessages ? rowData.numMessages : 0}</span>
                        {rowData.numUnreadMessages ? (
                          <span className="text-xs ml-1 text-green-700 font-medium py-1 px-2 bg-green-100 rounded-full">
                            {rowData.numUnreadMessages} New
                          </span>
                        ) : null}
                      </>
                    ),
                  };
                  return (
                    <TableRow
                      handleCloseTooltip={() => {}}
                      handleOpenTooltip={() => {}}
                      tooltipVisible={true}
                      tooltipOptions={moreOptions}
                      data={Object.values(sortedData)}
                      key={index}
                      id={index.toString()}
                      extraClassesRow={headers.map(header => header.extraClasses)}
                      handleTooltipClick={() => handleRowClick(rowData.requestId, rowData.officeId)}
                      onRowClick={() => handleRowClick(rowData.requestId, rowData.officeId)}
                    />
                  );
                })}
                <Pagination
                  colSpan={10}
                  count={userRequest.data ? userRequest.data.totalElements : 0}
                  onPageChange={onPageChange}
                  onRowsPerPageChange={onRowsPerPageChange}
                  page={tableState.pageNumber}
                  rowsPerPage={tableState.rowsPerPage}
                />
              </>
            ) : (
              <DataNotFound image={NoRequestFound} text="No requests found" />
            )}
          </div>
        </div>
        {user && showEditPatientProfile ? (
          <UserSideBar
            onClickOutside={() => {
              !isLoading ? setShowEditPatientProfile(false) : null;
            }}
            title={`${user?.firstName} ${user?.lastName ?? ""}`}
            visible={true}
          >
            <EditPatientProfile
              userData={user}
              isEditable={true}
              onClickCancel={val => {
                setShowEditPatientProfile(false);
                if (val && patientId && id) getUser(id, patientId);
              }}
              setIsLoading={setIsLoading}
              isLoading={isLoading}
            />
          </UserSideBar>
        ) : null}
      </main>
    </>
  );
};

export default ViewPatient;
